import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { WorkspaceLayout } from '../../components/layout/WorkspaceLayout';
import { FormSubmissionsPanel } from '../../features/requests/FormSubmissionsPanel';
import { FeatureDisabledPanel } from '../../features/workspace/FeatureDisabledPanel';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { fetchProjectById, setSelectedProjectId } from '../../redux/slices/projectsSlice';
import { hasProjectFeature } from '../../utils/projectFeatures';

export const FormSubmissionsPage = () => {
  const { projectId = '', formId = '' } = useParams();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [status, setStatus] = useState('loading');
  const { items } = useAppSelector((state) => state.projects);
  const project = items.find((item) => item.id === projectId) || null;
  const initialForm = location.state?.form?.id === formId ? location.state.form : null;

  useEffect(() => {
    if (!projectId) {
      setStatus('not-found');
      return;
    }

    let cancelled = false;
    setStatus('loading');
    dispatch(setSelectedProjectId(projectId));

    dispatch(fetchProjectById(projectId))
      .unwrap()
      .then(() => {
        if (!cancelled) {
          setStatus('ready');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('not-found');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, projectId]);

  if (status === 'not-found' || (!project && status !== 'loading')) {
    return <div className="p-10 text-center text-lg">{t('projectNotFound')}</div>;
  }

  const layoutProject = project || { id: projectId, name: t('requestsTitle') };
  const isWorkspaceLoading = status === 'loading';
  const databaseEnabled = hasProjectFeature(project, 'database');

  return (
    <WorkspaceLayout
      project={layoutProject}
      backTo={`/workspace/${projectId}/requests`}
      backAriaLabel={t('requestsBackToForms')}
      activeSection="database"
    >
      {!isWorkspaceLoading && !databaseEnabled ? (
        <FeatureDisabledPanel
          title={t('featuresDatabaseDisabledTitle')}
          body={t('featuresDatabaseDisabledBody')}
          actionLabel={t('backToWorkspace')}
          onAction={() => navigate(`/workspace/${projectId}`)}
        />
      ) : (
        <FormSubmissionsPanel
          projectId={projectId}
          formId={formId}
          initialForm={initialForm}
          forceLoading={isWorkspaceLoading}
          disableFetch={isWorkspaceLoading || !databaseEnabled}
        />
      )}
    </WorkspaceLayout>
  );
};
